import type { Reducer } from "@reduxjs/toolkit";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

import * as DrawingActions from "./actions";
import type { DrawingAction, DrawingStoreState } from "./types";

export type IDrawingAction = ActionType<typeof DrawingActions>;

const initialState: DrawingStoreState = {
  isFetching: false,
  hasError: false,

  drawingDetailsFetched: false,
  coordinatesFetched: false,

  currentMachine: null!,
  currentDrawing: null!,
  currentDrawingPath: null!,
  isCurrentDrawingImageAvailable: false,
  isCurrentDrawingImageFetching: false,

  drawingDetails: {
    imageName: null!,
    imagePath: null!,
    width: null!,
    height: null!
  },
  isFetchingDrawingDetails: false,
  hasDrawingDetailsError: false,

  drawingCoordinates: [],
  isFetchingDrawingCoordinates: false,
  hasDrawingCoordinatesError: false,

  drawingPartInformation: {
    count: 0,
    data: null!,
    header: null!
  },
  isFetchingDrawingPartsInformation: false,
  hasDrawingPartInformationError: false,

  selectedDrawingPart: null!
};

const drawingReducer: Reducer<DrawingStoreState, DrawingAction> = (state = initialState, action: IDrawingAction) => {
  switch (action.type) {
    // set current sku
    case getType(DrawingActions.updateCurrentMachineSuccess):
      return { ...state, currentMachine: action.payload };

    case getType(DrawingActions.updateCurrentMachineFail):
      return { ...state, isFetching: false, hasError: true };

    // drawing details
    case getType(DrawingActions.fetchDrawingDetails):
      return { ...state, isFetching: true, isFetchingDrawingDetails: true };

    case getType(DrawingActions.fetchDrawingDetailsSuccess): {
      const hasDimensionsError = action.payload && (!action.payload.height || !action.payload.width);
      return {
        ...state,
        isFetching: false,
        isFetchingDrawingDetails: false,
        drawingDetailsFetched: true,
        drawingDetails: action.payload,
        hasError: hasDimensionsError,
        hasDrawingDetailsError: hasDimensionsError,
        currentDrawing: action.payload.imageName,
        currentDrawingPath: action.payload.imagePath
      };
    }

    case getType(DrawingActions.fetchDrawingDetailsFail):
      return { ...state, isFetching: false, isFetchingDrawingDetails: false, hasError: true, hasDrawingDetailsError: true };

    // drawingCoordinates
    case getType(DrawingActions.fetchDrawingCoordinatesStart):
      return { ...state, isFetching: true, isFetchingDrawingCoordinates: true };

    case getType(DrawingActions.fetchDrawingCoordinatesSuccess): {
      const hasCoordsError = action.payload && action.payload.length === 0;
      return {
        ...state,
        isFetching: false,
        isFetchingDrawingCoordinates: false,
        coordinatesFetched: true,
        drawingCoordinates: action.payload,
        hasError: hasCoordsError,
        hasDrawingCoordinatesError: hasCoordsError
      };
    }

    case getType(DrawingActions.fetchDrawingCoordinatesFail):
      return { ...state, isFetching: false, isFetchingDrawingCoordinates: false, hasError: true, hasDrawingCoordinatesError: true };

    // drawingPartInformation
    case getType(DrawingActions.fetchDrawingPartInformationStart):
      return { ...state, isFetching: true, isFetchingDrawingPartsInformation: true };

    case getType(DrawingActions.fetchDrawingPartInformationSuccess):
      return { ...state, isFetching: false, isFetchingDrawingPartsInformation: false, drawingPartInformation: action.payload };

    case getType(DrawingActions.fetchDrawingPartInformationFail):
      return { ...state, isFetching: false, isFetchingDrawingPartsInformation: false, hasError: true, hasDrawingPartInformationError: true };

    // set selected drawing part
    case getType(DrawingActions.setSelectedDrawingPart): {
      const { drawingPartInformation } = state;
      // if the following data is missing an error message could be appropriate...
      const partInformationData = drawingPartInformation.data ? drawingPartInformation.data : null;
      const selectedPartInformation = partInformationData ? partInformationData.find((data) => data.part_number === action.payload.partNumber) : null;

      // deselect (could be a separate action...)
      if (!action.payload.partNumber || !action.payload.uId) {
        return {
          ...state,
          selectedDrawingPart: null!
        };
      }

      return {
        ...state,
        selectedDrawingPart: {
          partNumber: action.payload.partNumber,
          uId: action.payload.uId,
          partInformation: selectedPartInformation ? selectedPartInformation : null!
        }
      };
    }

    // reset reducer state
    case getType(DrawingActions.resetClickableDrawingsState): {
      const resetState = Object.assign({}, initialState);
      resetState.currentMachine = state.currentMachine;
      return resetState;
    }

    case getType(DrawingActions.fetchDrawingImageByNameStart):
      return { ...state, isCurrentDrawingImageFetching: true };

    case getType(DrawingActions.fetchDrawingImageByNameSuccess):
      return { ...state, isCurrentDrawingImageFetching: false, isCurrentDrawingImageAvailable: true };

    case getType(DrawingActions.fetchDrawingImageByNameFail):
      return { ...state, isCurrentDrawingImageFetching: false, isCurrentDrawingImageAvailable: false, hasError: true };

    default:
      return state;
  }
};

export default drawingReducer;
